import React, { useEffect, useMemo, useState } from "react";
import { clone, isEmpty, isNil } from "ramda";

import { MapContainer, TileLayer, Polyline, useMap, Pane } from "react-leaflet";
import { getMeasure, Legend, shouldAddPointsTotal } from "./utils";
import { LegendContentRight } from "../../styled";
import { Button, Dialog, DialogActions, DialogContent, MenuItem, TextField } from "@mui/material";

const MapDob = ({
  assetData,
  spatialDataList,
  dateTimeList,
  measureName,
  differences,
  scales,
  chartSpatialData,
  percentiles,
  equipmentData,
  intl,
}) => {
  const mapType = "regular";
  const tColorScale = scales["temperature"];
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [newPercentiles, setNewPercentiles] = useState(null);
  const differencesForTempLength = useState([
    { range: 5000, multiplier: 1 },
    { range: 15000, multiplier: 2 },
    { range: 25000, multiplier: 3 },
  ]);
  const optionList = useMemo(() => {
    const options = [];
    if (!isNil(chartSpatialData)) {
      for (let indexSeries = 0; indexSeries < chartSpatialData.series?.length; indexSeries++) {
        options.push({ name: chartSpatialData.series[indexSeries].name, type: "series", index: indexSeries });
      }
      for (let indexBathymetry = 0; indexBathymetry < chartSpatialData.bathymetry?.length; indexBathymetry++) {
        options.push({
          name: chartSpatialData.bathymetry[indexBathymetry].name,
          type: "bathymetry",
          index: indexBathymetry,
        });
      }
      for (let indexZones = 0; indexZones < chartSpatialData.zones?.length; indexZones++) {
        options.push({ name: chartSpatialData.zones[indexZones].name, type: "zones", index: indexZones });
      }
    }
    return options;
  }, [chartSpatialData]);
  const [selectedOption, setSelectedOption] = useState(null);
  useEffect(() => {
    if (!isNil(optionList) && !isEmpty(optionList)) setSelectedOption(optionList[0]);
  }, [optionList]);
  function ChangeView() {
    const mapView = useMap();
    useEffect(() => {
      mapView.dragging.enable();
      mapView.scrollWheelZoom.enable();
      mapView.doubleClickZoom.enable();
      mapView.boxZoom.enable();
      mapView.keyboard.enable();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapView]);

    //if (!isEmpty(alertPosition)) {
    //  mapView.setMaxBounds(null);
    //  mapView.setView(alertPosition, 15, { reset: false });
    //}

    return null;
  }

  const polylines = useMemo(() => {
    if (!isNil(spatialDataList) && !isNil(assetData) && !isNil(selectedOption)) {
      const filteredPoints = [];
      const visualIndexes = [];
      const realIndex = selectedOption.index;
      const type = selectedOption.type;
      const values =
        type !== "zones" ? chartSpatialData[type][realIndex].data : chartSpatialData[type][realIndex].markArea.data;
      const realValues = values.map((value) => value[1]);
      const cleanValues = realValues.filter((v) => !Number.isNaN(v));
      const max = Math.max(...cleanValues) > 0 ? 0 : Math.max(...cleanValues);
      const min = Math.min(...cleanValues);
      if (!isNil(spatialDataList[realIndex]) && !isEmpty(spatialDataList[realIndex])) {
        filteredPoints.push([]);

        if (type === "zones") {
          const ranges = values
            .map((area) => ({
              start: area[0].xAxis,
              end: area[1].xAxis,
              color: area[0].itemStyle.color,
            }))
            .sort((a, b) => a.start - b.start);
          let rangeIndex = 0;
          for (let index = 0; index < spatialDataList[realIndex].coordinates.length; index++) {
            let currentColor = null;

            while (rangeIndex < ranges.length && index > ranges[rangeIndex].end) {
              rangeIndex++;
            }

            if (rangeIndex < ranges.length && index >= ranges[rangeIndex].start && index <= ranges[rangeIndex].end) {
              currentColor = ranges[rangeIndex].color;
            }
            if (index === 0 || index % 10 === 0) {
              filteredPoints[0].push({
                pk: spatialDataList[realIndex].pk[index] / 1000,
                latitude: spatialDataList[realIndex].coordinates[index][0],
                longitude: spatialDataList[realIndex].coordinates[index][1],
                value: values[index],
                index: index,
                withColor: index === 0 || index % 10 === 0 ? true : false,
                max: max,
                color: currentColor?.replace(/rgba\((.+?),\s*[\d.]+\)/, "rgb($1)"),
                type: type,
                min: min,
              });
            }
          }
        } else {
          let prevValues = {
            state: null,
            values: values[0][1], //primer valor de la magnitud,
            withColor: false,
          };
          let mult = 4;
          const differenceMultiplier = measureName === "temperature" ? 1 * mult : 6;
          for (let indexDiff = 0; indexDiff < differencesForTempLength[0].length; indexDiff++) {
            if (spatialDataList[realIndex].coordinates.length < differencesForTempLength[0][indexDiff].range) {
              mult = differencesForTempLength[0][indexDiff].multiplier;
            }
          }
          for (let index = 0; index < spatialDataList[realIndex].coordinates.length; index++) {
            let shouldAdd = false;
            if (isEmpty(visualIndexes) && (index === 0 || index % 10 === 0)) {
              filteredPoints[0].push({
                pk: spatialDataList[realIndex].pk[index] / 1000,
                latitude: spatialDataList[realIndex].coordinates[index][0],
                longitude: spatialDataList[realIndex].coordinates[index][1],
                value: values[index][1],
                index: index,
                withColor: index === 0 || index % 10 === 0 ? true : false,
                max: max,
                type: type,
                min: min,
              });
            } else {
              shouldAdd = shouldAddPointsTotal({
                indexes: [],
                index: index,
                pk: spatialDataList[realIndex].pk[index],
                difference: differences[measureName] * differenceMultiplier,
                value: values[index][1],
                temporalIndexes: [],
                prevValues: null,
                filteredPoints: filteredPoints[0],
                visualIndexes: [],
              });
              if (shouldAdd) {
                filteredPoints[0].push({
                  pk: spatialDataList[realIndex].pk[index],
                  value: values[index][1],
                  index: index,
                  withColor: prevValues.withColor,
                  type: type,
                  max: max,
                  min: min,
                });
              }
            }
          }
        }
      }

      const pList = [];
      for (let indexDTList = 0; indexDTList < dateTimeList.length; indexDTList++) {
        if (!isNil(filteredPoints[indexDTList])) {
          pList.push([]);
          for (let i = 0; i < filteredPoints[indexDTList].length - 1; i++) {
            const p1 = clone(filteredPoints[indexDTList][i]);
            const p2 = clone(filteredPoints[indexDTList][i + 1]);
            pList[indexDTList].push({ p1, p2, withColor: p1.withColor });
          }
        }
      }
      return pList;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetData, spatialDataList, selectedOption]);

  useEffect(() => {
    if (isNil(selectedOption) || isNil(chartSpatialData) || isNil(chartSpatialData[selectedOption.type])) {
      return;
    }

    const realIndex = selectedOption.index;
    const type = selectedOption.type;

    const source = chartSpatialData[type]?.[realIndex];

    if (!source) return;

    const values = type !== "zones" ? source.data : source.markArea?.data;

    if (!values || values.length === 0) return;
    const realValues = values.map((value) => value[1]);

    const cleanValues = realValues.filter((v) => !Number.isNaN(v));

    if (cleanValues.length === 0) return;

    const maxRaw = Math.max(...cleanValues);
    const minRaw = Math.min(...cleanValues);

    const max = maxRaw > 0 ? 0 : maxRaw;
    const min = minRaw;

    if (min > max) return;

    setNewPercentiles({
      [measureName]: [min, max],
    });
  }, [selectedOption, chartSpatialData, measureName]);

  const MapContent = ({ height = "40vh", width = "47vw" }) => (
    <MapContainer
      id={`map-for-${measureName}`}
      style={{ width: width, height: height }}
      center={assetData.centroid}
      zoom={11}
      rotateControl={false}
    >
      <TileLayer
        attribution={window.RUNTIME_CONFIG.IS_ONLINE ? "Google Maps" : "OpenStreetMap"}
        url={window.RUNTIME_CONFIG.MAP_TYPES.find((type) => type.value === mapType).tileLayer}
        maxZoom={18}
        subdomains={["mt0", "mt1", "mt2", "mt3"]}
      />
      <Pane name="polygonsPane" style={{ zIndex: 400 }} />

      {!isNil(optionList) && !isNil(selectedOption) && (
        <LegendContentRight>
          <TextField
            select
            id="alertType"
            margin="dense"
            sx={{ width: "9rem" }}
            label={intl.formatMessage({ id: "data.visualization" })}
            required
            variant="outlined"
            value={selectedOption}
            onChange={(event) => {
              setSelectedOption(event.target.value);
            }}
          >
            {optionList.map((option, optionIndex) => (
              <MenuItem key={`option_${option.name}_${optionIndex}`} value={option}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </LegendContentRight>
      )}
      {!isFullscreen && (
        <Button
          variant="contained"
          size="small"
          onClick={() => setIsFullscreen(true)}
          style={{
            position: "absolute",
            top: "19.3rem",
            right: "0.7rem",
            zIndex: 1000,
          }}
        >
          {intl.formatMessage({ id: "maximize" })}
        </Button>
      )}

      {!isNil(newPercentiles) && !isNil(selectedOption) && (
        <Legend
          assetData={assetData}
          equipmentData={equipmentData}
          measureName={measureName}
          scales={scales}
          percentiles={newPercentiles}
          type={selectedOption.type}
          intl={intl}
        />
      )}
      <Pane name="linesPane" style={{ zIndex: 500 }} />
      <ChangeView />
      {!isNil(polylines) &&
        polylines.map((polyline, polyIndex) => {
          return polyline.map((line, lineIndex) => {
            const color =
              selectedOption.type !== "zones"
                ? tColorScale(
                    getMeasure(
                      polylines[polyIndex][lineIndex].p1.value,
                      line.p1.min,
                      line.p1.max,
                      null,
                      tColorScale.domain(),
                    ),
                  )
                : isNil(line.p1.color)
                  ? "green"
                  : line.p1.color;
            return (
              <Polyline
                key={`line-${polyIndex}-${lineIndex}-${line.p1.type}`}
                positions={[
                  [line.p1.latitude, line.p1.longitude],
                  [line.p2.latitude, line.p2.longitude],
                ]}
                weight={8}
                color={color}
                pane="linesPane"
              />
            );
          });
        })}
    </MapContainer>
  );
  return (
    <>
      <MapContent />
      <Dialog open={isFullscreen} maxWidth="xl" fullWidth onClose={() => setIsFullscreen(false)}>
        <DialogContent style={{ height: "80vh" }}>
          <MapContent width="100%" height="100%" />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsFullscreen(false)}>{intl.formatMessage({ id: "close" })}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default MapDob;
